import { request } from "./request";

export default class UserAPI {
  static root() {
    return "/api/v1/admin/kyc/creators";
  }

  /**
   * Get user KYC list
   * @param {Object} data - Request data: { page, limit, search, status, lutStatus, fircOnly }
   * When `lutStatus` is passed the backend lists LUT (FIRC export-earnings
   * compliance) reviews filtered by lutVerificationStatus instead of main KYC
   * reviews (TMG-2249). `fircOnly` narrows the list to the creators FIRC can
   * apply to — Indian creators on their own whitelabel host (TMG-3495).
   */
  static getUserKYCList(data) {
    const { page, limit = 10, search = "", status, lutStatus, fircOnly } = data;

    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...(search && { search }),
      ...(status && { status }),
      ...(lutStatus && { lutStatus }),
      ...(fircOnly && { fircOnly: "true" }),
    });

    return request({
      url: `${this.root()}?${query.size > 0 ? query : ""}`,
      method: "GET",
    });
  }

  /**
   * Verify/reject a creator's LUT for FIRC export-earnings compliance (TMG-2249).
   * On verification the LUT number is promoted onto the user and FIRC is enabled
   * permanently (irreversible by design).
   * @param {string} creatorId - Creator (user) id to verify the LUT for
   * @param {Object} data - { status: 'verified' | 'rejected', rejectionReason?: string }
   */
  static verifyCreatorLUT(creatorId, data) {
    const { status, rejectionReason = "" } = data;

    return request({
      url: `${this.root()}/${creatorId}/lut/verify`,
      method: "POST",
      data: {
        status,
        // rejection reasons are persisted only when the LUT is rejected
        ...(status === "rejected"
          ? { rejectionReasons: rejectionReason ? [rejectionReason] : [] }
          : {}),
      },
    });
  }

  /**
   * Toggle automatic withdrawals for a user
   * @param {string} userId - User ID to toggle automatic withdrawals for
   * @param {boolean} enabled - Whether to enable or disable automatic withdrawals
   */
  static toggleAutomaticWithdrawals(userId, enabled) {
    return request({
      url: `${this.root()}/${userId}/automatic-withdrawals`,
      method: "POST",
      data: {
        enable: enabled,
      },
    });
  }

  /**
   * Verify user KYC by user ID
   * @param {string} userId - User ID to verify KYC for
   * @param {Object} data - Request data: { status, rejectionReasons: { pan, aadhar, passport, residency } }
   */
  static verifyUserKYC(userId, data) {
    const { status, rejectionReasons } = data;

    const {
      pan = "",
      authorisedSignatoryPan = "",

      aadhar = "",
      authorisedSignatoryAadhar = "",

      passport = "",
      authorisedSignatoryPassport = "",

      residency = "",
      authorisedSignatoryResidency = "",

      certificateOfIncorporation = "",

      gstCertificate = "",
    } = rejectionReasons;

    return request({
      url: `${this.root()}/${userId}/verify`,
      method: "POST",
      data: {
        status,
        rejectionReasons: {
          pan: pan ? [pan] : [],
          authorisedSignatoryPan: authorisedSignatoryPan
            ? [authorisedSignatoryPan]
            : [],

          aadhar: aadhar ? [aadhar] : [],
          authorisedSignatoryAadhar: authorisedSignatoryAadhar
            ? [authorisedSignatoryAadhar]
            : [],

          passport: passport ? [passport] : [],
          authorisedSignatoryPassport: authorisedSignatoryPassport
            ? [authorisedSignatoryPassport]
            : [],

          residency: residency ? [residency] : [],
          authorisedSignatoryResidency: authorisedSignatoryResidency
            ? [authorisedSignatoryResidency]
            : [],

          certificateOfIncorporation: certificateOfIncorporation
            ? [certificateOfIncorporation]
            : [],

          gstCertificate: gstCertificate ? [gstCertificate] : [],
          // ...(pan && { pan: [pan] }),
          // ...(authorisedSignatoryPan && {
          //   authorisedSignatoryPan: [authorisedSignatoryPan],
          // }),
          // ...(aadhar && { aadhar: [aadhar] }),
          // ...(authorisedSignatoryAadhar && {
          //   authorisedSignatoryAadhar: [authorisedSignatoryAadhar],
          // }),
          // ...(passport && { passport: [passport] }),
          // ...(authorisedSignatoryPassport && {
          //   authorisedSignatoryPassport: [authorisedSignatoryPassport],
          // }),
          // ...(residency && { residency: [residency] }),
          // ...(authorisedSignatoryResidency && {
          //   authorisedSignatoryResidency: [authorisedSignatoryResidency],
          // }),
          // ...(certificateOfIncorporation && {
          //   certificateOfIncorporation: [certificateOfIncorporation],
          // }),
          // ...(gstCertificate && { gstCertificate: [gstCertificate] }),
        },
      },
    });
  }

  /**
   * Get KYC document stream by document id (secured url) as a blob
   * (for secured documents like PAN, Aadhar, other KYC documents etc)
   */
  static getKYCDocumentStreamById(userId, documentId) {
    return request({
      url: `${this.root()}/${userId}/documents/${documentId}`,
      method: "GET",
      responseType: "blob",
    });
  }
}
